import styled, { keyframes } from 'styled-components'
import { Link } from 'react-router-dom'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const scaleIn = keyframes`
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`

export const Container = styled.main`
  min-height: calc(100vh - 52px);
  animation: ${fadeIn} 0.6s ease-out;
`

export const HeroSection = styled.section`
  padding: 60px 20px;
  background: ${({ theme }) => theme.colors.heroGradient};
  color: white;
  animation: ${fadeIn} 0.8s ease-out;

  .hero-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    gap: 40px;

    @media (max-width: 720px) {
      flex-direction: column;
    }
  }

  section {
    flex: 1;
    animation: ${fadeInUp} 0.8s ease-out 0.2s both;
    color: white;

    h1 {
      font-size: 2.5rem;
      margin-bottom: 15px;
      line-height: 1.2;
      color: white;

      @media (max-width: 720px) {
        font-size: 2rem;
        margin-top: 20px;
      }
    }

    .subtitle {
      font-size: 1.3rem;
      margin-bottom: 20px;
      opacity: 0.95;
      font-weight: 500;

      @media (max-width: 720px) {
        font-size: 1.1rem;
      }
    }

    p {
      font-size: 1.1rem;
      line-height: 1.6;
      margin-bottom: 30px;
      opacity: 0.9;
      color: white;

      @media (max-width: 720px) {
        font-size: 1rem;
      }
    }
  }
`

export const Hero = styled.img`
  width: 300px;
  flex-shrink: 0;
  animation: ${scaleIn} 0.8s ease-out 0.4s both;

  &.hero-image-left {
    display: none;

    @media (max-width: 720px) {
      display: block;
      width: 100%;
      max-width: 300px;
    }
  }

  &.hero-image-right {
    @media (max-width: 720px) {
      display: none;
    }
  }
`

export const CTAButton = styled(Link)`
  display: inline-block;
  padding: 15px 40px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.colors.cardShadow};
    background: ${({ theme }) => theme.colors.surfaceElevated};
  }

  &:active {
    transform: translateY(0);
  }
`

export const StatsSection = styled.section`
  max-width: 1200px;
  margin: -40px auto 0;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  position: relative;
  z-index: 1;

  @media (max-width: 720px) {
    margin-top: -30px;
  }
`

export const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  animation: ${scaleIn} 0.6s ease-out;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }

  h3 {
    font-size: 2.5rem;
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 10px;
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 1rem;
  }
`

export const CategoriesSection = styled.section`
  max-width: 1200px;
  margin: 80px auto;
  padding: 0 20px;
  animation: ${fadeInUp} 0.8s ease-out;

  h2 {
    text-align: center;
    font-size: 2.2rem;
    margin-bottom: 15px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  .section-description {
    text-align: center;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 1.1rem;
    margin-bottom: 40px;
  }

  .categories-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 25px;

    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }
`

export const CategoryCard = styled(Link)`
  background: ${({ theme }) => theme.colors.surface};
  padding: 30px;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  border: 2px solid ${({ theme }) => theme.colors.border};
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.6s ease-out;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &:hover {
    transform: translateY(-5px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.colors.cardShadow};

    .category-icon {
      transform: scale(1.2) rotate(10deg);
    }
  }

  .category-icon {
    font-size: 3rem;
    margin-bottom: 15px;
    transition: transform 0.3s ease;
  }

  h3 {
    font-size: 1.4rem;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.5;
    font-size: 0.95rem;
  }
`

export const FeaturesSection = styled.section`
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'linear-gradient(135deg, #131d32 0%, #111827 100%)' : theme.colors.surfaceMuted};
  padding: 80px 20px;
  animation: ${fadeIn} 0.8s ease-out;

  h2 {
    text-align: center;
    font-size: 2.2rem;
    margin-bottom: 50px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  .features-grid {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 30px;

    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }
`

export const FeatureCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s ease;
  animation: ${scaleIn} 0.6s ease-out;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

    .feature-icon {
      transform: scale(1.2);
    }
  }

  .feature-icon {
    font-size: 3rem;
    margin-bottom: 15px;
    display: block;
    transition: transform 0.3s ease;
  }

  h3 {
    font-size: 1.2rem;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.5;
    font-size: 0.95rem;
  }
`

export const FeaturedCalculatorsSection = styled.section`
  max-width: 1200px;
  margin: 80px auto;
  padding: 0 20px;
  animation: ${fadeInUp} 0.8s ease-out;

  h2 {
    text-align: center;
    font-size: 2.2rem;
    margin-bottom: 15px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  .section-description {
    text-align: center;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 1.1rem;
    margin-bottom: 40px;
  }

  .calculators-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 25px;

    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }
`

export const CalculatorCard = styled(Link)`
  background: ${({ theme }) => theme.colors.surface};
  padding: 30px;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  border: 2px solid ${({ theme }) => theme.colors.border};
  position: relative;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.6s ease-out;

  &:hover {
    transform: translateY(-5px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.colors.cardShadow};

    .arrow {
      transform: translateX(5px);
    }
  }

  .category-badge {
    display: inline-block;
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.onPrimary};
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    margin-bottom: 15px;
  }

  h3 {
    font-size: 1.3rem;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.5;
    margin-bottom: 15px;
  }

  .arrow {
    position: absolute;
    bottom: 20px;
    right: 20px;
    font-size: 1.5rem;
    color: ${({ theme }) => theme.colors.primary};
    transition: transform 0.3s ease;
  }
`

export const HowItWorksSection = styled.section`
  background: ${({ theme }) => theme.colors.heroGradient};
  padding: 80px 20px;
  color: white;
  animation: ${fadeIn} 0.8s ease-out;

  h2 {
    text-align: center;
    font-size: 2.2rem;
    margin-bottom: 15px;
    color: white;
  }

  .section-description {
    text-align: center;
    font-size: 1.1rem;
    margin-bottom: 50px;
    opacity: 0.95;
    color: white;
  }

  .steps-container {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 40px;

    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }
`

export const Step = styled.div`
  text-align: center;
  animation: ${scaleIn} 0.6s ease-out;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-10px);

    .step-number {
      transform: scale(1.1);
      background: ${({ theme }) => theme.colors.surface};
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  .step-number {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
    font-weight: bold;
    margin: 0 auto 20px;
    transition: all 0.3s ease;
  }

  h3 {
    font-size: 1.3rem;
    margin-bottom: 10px;
    color: white;
  }

  p {
    opacity: 0.9;
    line-height: 1.5;
    color: white;
  }
`

export const QuickLinksSection = styled.section`
  max-width: 1200px;
  margin: 80px auto;
  padding: 0 20px 80px;
  animation: ${fadeInUp} 0.8s ease-out;

  h2 {
    text-align: center;
    font-size: 2.2rem;
    margin-bottom: 40px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  .quick-links-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;

    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }
`

export const QuickLink = styled(Link)`
  background: ${({ theme }) => theme.colors.surface};
  padding: 20px 25px;
  border-radius: 8px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 2px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  font-weight: 500;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.6s ease-out;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    transform: translateX(5px);
    box-shadow: ${({ theme }) => theme.colors.cardShadow};
  }
`
